<template>
    <div>
        <div v-if="label" class="mb-1">
            <label class="text-subtitle-2">{{ label }}</label>
        </div>

        <v-text-field :value="display" :aria-label="ariaLabel" type="text" variant="outlined" :prefix="prefix"
            :inputmode="inputmode" :pattern="pattern" :autocomplete="autocomplete" :hide-details="hideDetails"
            :class="inputClass" :disabled="disabled" :readonly="readonly" @keydown="onKeydown" @input="onInput"
            @paste="onPaste"></v-text-field>
    </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';

const props = defineProps({
    value: { type: [String, Number], default: '' }, // accepts formatted or backend numeric
    label: { type: String, default: '' },
    ariaLabel: { type: String, default: 'Valor (dinheiro)' },
    prefix: { type: String, default: 'R$' },
    inputmode: { type: String, default: 'numeric' },
    pattern: { type: String, default: '[0-9]*' },
    autocomplete: { type: String, default: 'off' },
    hideDetails: { type: [Boolean, String], default: 'auto' },
    inputClass: { type: [String, Object, Array], default: 'mb-3' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
});

const emit = defineEmits(['update:value', 'raw', 'change', 'input']);

// only digits, cents on right. ex "123456" -> 1.234,56
const digits = ref('');

// display formatted "1.234,56"
const display = computed(() => formatFromDigits(digits.value));

// helpers
function formatFromDigits(d) {
    if (!d || d.length === 0) return '0,00';
    const decPart = d.slice(-2).padStart(2, '0');
    const intPart = d.slice(0, -2) || '0';
    const intWithSep = String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${intWithSep},${decPart}`;
}

function digitsFromFormattedOrBackend(val) {
    if (val == null || val === '') return '';
    const s = String(val).trim();
    // if contains comma assume formatted "1.234,56"
    if (s.includes(',')) {
        return s.replace(/\D/g, '');
    }
    // if contains dot and no comma treat as backend numeric "1234.56" or "1234"
    if (s.includes('.')) {
        const normalized = s.replace(',', '.');
        const num = Number(normalized);
        if (Number.isNaN(num)) return '';
        return String(Math.round(num * 100));
    }
    // plain digits
    return s.replace(/\D/g, '');
}

function backendFromDigits(d) {
    if (!d || d.length === 0) return '0.00';
    const num = Number(d) / 100;
    return num.toFixed(2);
}

// initialize from prop
onMounted(() => {
    digits.value = digitsFromFormattedOrBackend(props.value);
});

// react to external changes
watch(() => props.value, (v) => {
    const newD = digitsFromFormattedOrBackend(v);
    if (newD !== digits.value) {
        digits.value = newD;
    }
});

// keyboard handling: digits append, Backspace removes last digit, allow navigation keys
function onKeydown(e) {
    const allowedNav = ['ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (e.ctrlKey || e.metaKey || e.altKey) return; // allow shortcuts
    if (allowedNav.includes(e.key)) return;
    if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        digits.value = (digits.value || '') + e.key;
        emitUpdate();
        return;
    }
    if (e.key === 'Backspace') {
        e.preventDefault();
        if (digits.value && digits.value.length > 0) {
            digits.value = digits.value.slice(0, -1);
        } else {
            digits.value = '';
        }
        emitUpdate();
        return;
    }
    // prevent anything else
    e.preventDefault();
}

// onInput handles auto-complete and mobile IME; extract digits and replace
function onInput(e) {
    const val = e?.target?.value ?? '';
    const extracted = String(val).replace(/\D/g, '');
    digits.value = extracted;
    emitUpdate();
}

// onPaste: append digits from clipboard
function onPaste(e) {
    e.preventDefault();
    const clipboard = (e.clipboardData || window.clipboardData);
    const text = clipboard ? clipboard.getData('text') : '';
    const extracted = String(text || '').replace(/\D/g, '');
    if (!extracted) return;
    digits.value = (digits.value || '') + extracted;
    emitUpdate();
}

function emitUpdate() {
    const formatted = formatFromDigits(digits.value);
    const rawBackend = backendFromDigits(digits.value);
    emit('update:value', formatted);
    emit('raw', rawBackend);
    emit('input', formatted);
    emit('change', formatted);
}
</script>

<style scoped>
/* minimal styling; leave rest to parent */
</style>
